import { useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import mqtt, { MqttClient, IClientOptions } from 'mqtt';

import { MQTT_URL, MQTT_USERNAME, MQTT_PASSWORD, buildTelemetryTopic } from '../../../config/mqttConfig';
import { WebSocketConnectionState } from '../../../hooks/useWebSocketConnection';

const MAX_RECONNECT_DELAY_MS = 30000;
const DISCONNECT_RESET_DELAY_MS = 100;

interface UseCrewMQTTOptions {
  deviceUuid: string;
  onTelemetryMessage: (data: unknown) => void;
  onError?: (error: string | null) => void;
  maxReconnectAttempts?: number;
  /** Override the default MQTT broker URL (must be a ws:// or wss:// URL) */
  mqttUrl?: string;
  /** Override the default MQTT username */
  mqttUsername?: string;
  /** Override the default MQTT password */
  mqttPassword?: string;
}

interface UseCrewMQTTReturn {
  connect: () => void;
  disconnect: () => void;
  connectionState: WebSocketConnectionState;
  reconnectAttempts: number;
}

export const useCrewMQTT = (options: UseCrewMQTTOptions): UseCrewMQTTReturn => {
  const { deviceUuid, onTelemetryMessage, onError, maxReconnectAttempts = 5, mqttUrl, mqttUsername, mqttPassword } = options;

  const { t } = useTranslation();
  const [connectionState, setConnectionState] = useState<WebSocketConnectionState>('disconnected');

  const isDisconnectingRef = useRef<boolean>(false);
  const clientRef = useRef<MqttClient | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const connectRef = useRef<(() => void) | null>(null);

  const callbacksRef = useRef({
    onTelemetryMessage,
    onError,
    deviceUuid
  });

  // Update callback ref whenever callbacks change
  useEffect(() => {
    callbacksRef.current = {
      onTelemetryMessage,
      onError,
      deviceUuid
    };
  }, [onTelemetryMessage, onError, deviceUuid]);

  const calculateReconnectDelay = (attempts: number): number => Math.min(1000 * 2 ** attempts, MAX_RECONNECT_DELAY_MS);

  const updateConnectionState = useCallback((state: WebSocketConnectionState) => {
    setConnectionState(state);
  }, []);

  const handleReconnect = useCallback(() => {
    if (isDisconnectingRef.current) return;

    const delay = calculateReconnectDelay(reconnectAttemptsRef.current);
    updateConnectionState('error');
    reconnectAttemptsRef.current++;

    reconnectTimeoutRef.current = setTimeout(() => {
      if (!isDisconnectingRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
        connectRef.current?.();
      } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        updateConnectionState('error');
        callbacksRef.current.onError?.(t('crew_view.errors.telemetry_connection_lost'));
      }
    }, delay);
  }, [maxReconnectAttempts, t, updateConnectionState]);

  const cleanupConnection = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (clientRef.current) {
      const client = clientRef.current;
      client.end(true);
      clientRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!deviceUuid) {
      console.warn('Cannot connect MQTT: deviceUuid is required');
      return;
    }

    cleanupConnection();
    updateConnectionState('connecting');

    const brokerUrl = mqttUrl || String(MQTT_URL);
    const clientOptions: IClientOptions = {
      username: mqttUsername ?? MQTT_USERNAME,
      password: mqttPassword ?? MQTT_PASSWORD,
      clientId: `crew_view_${deviceUuid}_${Date.now()}`,
      clean: true,
      reconnectPeriod: 0, // We handle reconnection manually
      connectTimeout: 10000,
      keepalive: 60
    };

    try {
      const client = mqtt.connect(brokerUrl, clientOptions);
      clientRef.current = client;

      client.on('connect', () => {
        reconnectAttemptsRef.current = 0;
        updateConnectionState('connected');
        callbacksRef.current.onError?.(null);

        // Subscribe to telemetry topic
        const topic = buildTelemetryTopic(deviceUuid);
        client.subscribe(topic, { qos: 1 }, (err: Error | null) => {
          if (err) {
            console.error('MQTT subscription error:', err);
            callbacksRef.current.onError?.(t('crew_view.errors.telemetry_connection_lost'));
          }
        });
      });

      client.on('message', (topic: string, message: Buffer | Uint8Array | string) => {
        try {
          let messageStr: string;
          if (typeof message === 'string') {
            messageStr = message;
          } else if (typeof Buffer !== 'undefined' && message instanceof Buffer) {
            messageStr = message.toString('utf8');
          } else if (message instanceof Uint8Array) {
            messageStr = new TextDecoder('utf8').decode(message);
          } else {
            messageStr = String(message);
          }

          const data = JSON.parse(messageStr);
          if (callbacksRef.current?.onTelemetryMessage) {
            callbacksRef.current.onTelemetryMessage(data);
          }
        } catch (error) {
          console.error('Error parsing MQTT telemetry message:', error, {
            topic,
            messageType: typeof message,
            messageLength: message?.length
          });
        }
      });

      client.on('close', () => {
        if (isDisconnectingRef.current) return;

        updateConnectionState('disconnected');

        const isLimitReached = reconnectAttemptsRef.current >= maxReconnectAttempts;
        if (!isLimitReached) {
          handleReconnect();
        } else {
          updateConnectionState('error');
          callbacksRef.current.onError?.(t('crew_view.errors.telemetry_connection_lost'));
        }
      });

      client.on('error', (error: Error) => {
        console.error('MQTT client error:', error);
        callbacksRef.current.onError?.(error.message || 'MQTT connection error');
        updateConnectionState('error');
      });

      client.on('offline', () => updateConnectionState('disconnected'));
      client.on('reconnect', () => updateConnectionState('connecting'));
    } catch (error) {
      console.error('Failed to create MQTT client:', error);
      updateConnectionState('error');
      callbacksRef.current.onError?.(error instanceof Error ? error.message : 'Failed to create MQTT client');
    }
  }, [deviceUuid, maxReconnectAttempts, cleanupConnection, updateConnectionState, t]);

  // Store connect function in ref for handleReconnect
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const disconnect = useCallback(() => {
    isDisconnectingRef.current = true;

    const hasPendingRetry = reconnectTimeoutRef.current !== null && reconnectAttemptsRef.current < maxReconnectAttempts;

    if (!hasPendingRetry) {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
    }

    cleanupConnection();

    setTimeout(() => {
      isDisconnectingRef.current = false;
    }, DISCONNECT_RESET_DELAY_MS);

    if (!hasPendingRetry) {
      updateConnectionState('disconnected');
    }
  }, [maxReconnectAttempts, cleanupConnection, updateConnectionState]);

  useEffect(() => {
    return () => disconnect();
  }, [disconnect]);

  return {
    connect,
    disconnect,
    connectionState,
    reconnectAttempts: reconnectAttemptsRef.current
  };
};
